// frontend/src/hooks/useMediaLibrary.ts
import { useState, useEffect, useCallback } from 'react';
import mediaService from '../services/mediaService';
import { MediaAsset } from '../types/Portfolio';

interface UseMediaLibraryReturn {
  assets: MediaAsset[];
  loading: boolean;
  uploading: boolean;
  error: string | null;
  total: number;
  page: number;
  totalPages: number;
  uploadFile: (file: File, metadata?: {
    category?: string;
    altText?: string;
    caption?: string;
    tags?: string[];
  }) => Promise<MediaAsset>;
  deleteAsset: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
}

export const useMediaLibrary = (initialLimit: number = 20): UseMediaLibraryReturn => {
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const loadAssets = useCallback(async (pageNum: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      const response = await mediaService.getAll({
        limit: initialLimit,
        page: pageNum
      });

      if (pageNum === 1) {
        setAssets(response.assets);
      } else {
        setAssets(prev => [...prev, ...response.assets]);
      }

      setTotal(response.total);
      setPage(response.page);
      setTotalPages(response.totalPages);
    } catch (err: any) {
      console.error('Error loading media assets:', err);
      setError(err.message || 'Failed to load media assets');
    } finally {
      setLoading(false);
    }
  }, [initialLimit]);

  useEffect(() => {
    loadAssets(1);
  }, [loadAssets]);

  const uploadFile = async (file: File, metadata?: {
    category?: string;
    altText?: string;
    caption?: string;
    tags?: string[];
  }): Promise<MediaAsset> => {
    try {
      setUploading(true);
      setError(null);

      const uploaded = await mediaService.upload(file, metadata);

      // Add to beginning of list
      setAssets(prev => [uploaded, ...prev]);
      setTotal(prev => prev + 1);

      return uploaded;
    } catch (err: any) {
      console.error('Error uploading file:', err);
      setError(err.message || 'Failed to upload file');
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const deleteAsset = async (id: string): Promise<void> => {
    try {
      setError(null);
      
      await mediaService.delete(id);

      // Remove from list
      setAssets(prev => prev.filter(asset => asset.id !== id));
      setTotal(prev => prev - 1);
    } catch (err: any) {
      console.error('Error deleting asset:', err);
      setError(err.message || 'Failed to delete asset');
      throw err;
    }
  };

  const refresh = async (): Promise<void> => {
    await loadAssets(1);
  };

  const loadMore = async (): Promise<void> => {
    if (page < totalPages) {
      await loadAssets(page + 1);
    }
  };

  return {
    assets,
    loading,
    uploading,
    error,
    total,
    page,
    totalPages,
    uploadFile,
    deleteAsset,
    refresh,
    loadMore
  };
};